import {
  adminOrderRowSchema,
  adminOrderSchema,
  type AdminOrder,
  type AdminOrderRow,
  type OrderChannel,
  type OrderStatus,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  Table,
  type Column,
} from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { formatEuros, PAYMENT_LABEL, STATUS_LOOK } from './labels';

const PAGE = 50;

export function OrderStatusBadge({ status }: { status: OrderStatus }) {
  const look = STATUS_LOOK[status];
  return <StatusBadge tone={look.tone}>{look.label}</StatusBadge>;
}

const COLUMNS: Column<AdminOrderRow>[] = [
  {
    key: 'number',
    header: 'Encomenda',
    cell: (o) => (
      <span className="flex flex-col">
        <span className="font-semibold">
          {o.number}
          {o.attention ? <span title={o.attention}> ⚠</span> : null}
        </span>
        <span className="text-caption text-gu-ink-muted">
          {o.customerLogin ?? (o.channel === 'in_person' ? 'Cliente ao balcão' : '—')} ·{' '}
          {formatDateTime(o.createdAt)}
        </span>
      </span>
    ),
  },
  { key: 'status', header: 'Estado', cell: (o) => <OrderStatusBadge status={o.status} /> },
  { key: 'total', header: 'Total', cell: (o) => formatEuros(o.totalCents), align: 'right' },
];

/** Orders, online and in person, with the selected one beside the list. */
export function OrdersView({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const [status, setStatus] = useState<OrderStatus | ''>('');
  const [channel, setChannel] = useState<OrderChannel | ''>('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<AdminOrderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => setOffset(0), [status, channel, query]);

  useEffect(() => {
    const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (status) params.set('status', status);
    if (channel) params.set('channel', channel);
    if (query.trim()) params.set('q', query.trim());
    const timer = setTimeout(() => {
      getJson(`/api/site/store/orders?${params.toString()}`, z.array(adminOrderRowSchema))
        .then((list) => {
          setError(null);
          setRows(list);
        })
        .catch((e: unknown) => setError(messageOf(e)));
    }, 250);
    return () => clearTimeout(timer);
  }, [status, channel, query, offset, version]);

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[28rem_minmax(0,1fr)]">
      <Card headingLevel={2} title="Encomendas">
        <Field label="Número">
          <Input placeholder="GU000123" value={query} onChange={(e) => setQuery(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Estado">
            <Select value={status} onChange={(e) => setStatus(e.target.value as OrderStatus | '')}>
              <option value="">Todos</option>
              {(Object.keys(STATUS_LOOK) as OrderStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LOOK[s].label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Canal">
            <Select
              value={channel}
              onChange={(e) => setChannel(e.target.value as OrderChannel | '')}
            >
              <option value="">Todos</option>
              <option value="online">Online</option>
              <option value="in_person">Ao balcão</option>
            </Select>
          </Field>
        </div>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <Table
          caption="Encomendas encontradas"
          columns={COLUMNS}
          rows={rows ?? []}
          rowKey={(o) => o.id}
          empty={rows ? 'Nenhuma encomenda.' : 'A carregar…'}
          onRowClick={(o) => onSelect(o.id)}
        />
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            Anteriores
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!rows || rows.length < PAGE}
            onClick={() => setOffset(offset + PAGE)}
          >
            Seguintes
          </Button>
        </div>
      </Card>
      {selectedId ? (
        <OrderDetail
          key={selectedId}
          orderId={selectedId}
          onChanged={() => setVersion((v) => v + 1)}
        />
      ) : (
        <p className="text-gu-ink-muted">Escolha uma encomenda para ver o detalhe.</p>
      )}
    </div>
  );
}

function OrderDetail({ orderId, onChanged }: { orderId: string; onChanged: () => void }) {
  const [order, setOrder] = useState<AdminOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getJson(`/api/site/store/orders/${orderId}`, adminOrderSchema)
      .then(setOrder)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [orderId]);

  const act = useCallback(
    async (path: string, body: unknown = {}) => {
      setBusy(true);
      setError(null);
      try {
        const updated = adminOrderSchema.parse(
          await send('POST', `/api/site/store/orders/${orderId}/${path}`, body),
        );
        setOrder(updated);
        onChanged();
      } catch (e) {
        setError(messageOf(e));
      } finally {
        setBusy(false);
      }
    },
    [orderId, onChanged],
  );

  if (!order) {
    return error ? (
      <Notice tone="danger">{error}</Notice>
    ) : (
      <p className="text-gu-ink-muted">A carregar…</p>
    );
  }

  const hasFee = order.lines.some((l) => l.productKind === 'membership_fee');

  return (
    <Card
      headingLevel={2}
      eyebrow={order.channel === 'online' ? 'Online' : 'Ao balcão'}
      title={`Encomenda ${order.number}`}
      actions={<OrderStatusBadge status={order.status} />}
    >
      <div className="flex flex-col gap-4">
        {order.attention ? (
          <Notice tone="warning" title="A ver">
            {order.attention}{' '}
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => void act('attention/clear')}
            >
              Resolvido
            </Button>
          </Notice>
        ) : null}
        {order.invoiceError ? (
          <Notice tone="warning" title="Fatura por emitir">
            {order.invoiceError}. A loja tenta de novo sozinha.
          </Notice>
        ) : null}
        {hasFee &&
        order.status !== 'pending_payment' &&
        order.status !== 'cancelled' &&
        !order.membershipSyncedAt ? (
          <Notice tone="warning" title="Quota por registar">
            A members-api ainda não confirmou a quota. A loja tenta de novo sozinha.
          </Notice>
        ) : null}

        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-1 text-body">
          <dt className="text-gu-ink-muted">Cliente</dt>
          <dd>
            {order.customer ? (order.customer.login ?? order.customer.accountId) : 'Sem conta'}
          </dd>
          <dt className="text-gu-ink-muted">Nível na compra</dt>
          <dd>{order.level}</dd>
          <dt className="text-gu-ink-muted">Pagamento</dt>
          <dd>
            {PAYMENT_LABEL[order.paymentMethod]}
            {order.paidAt ? ` · pago ${formatDateTime(order.paidAt)}` : ''}
            {order.receivedBy ? ` · recebido por ${order.receivedBy}` : ''}
          </dd>
          {order.createdBy ? (
            <>
              <dt className="text-gu-ink-muted">Registada por</dt>
              <dd>{order.createdBy}</dd>
            </>
          ) : null}
          {order.billing.nif || order.billing.name ? (
            <>
              <dt className="text-gu-ink-muted">Faturar a</dt>
              <dd>
                {[order.billing.name, order.billing.nif && `NIF ${order.billing.nif}`]
                  .filter(Boolean)
                  .join(' · ')}
              </dd>
            </>
          ) : null}
          <dt className="text-gu-ink-muted">Fatura</dt>
          <dd>{order.invoice ? order.invoice.number : '—'}</dd>
          {order.holdUntil && order.status === 'pending_payment' ? (
            <>
              <dt className="text-gu-ink-muted">Reservada até</dt>
              <dd>{formatDateTime(order.holdUntil)}</dd>
            </>
          ) : null}
          {order.cancelReason ? (
            <>
              <dt className="text-gu-ink-muted">Cancelada</dt>
              <dd>{order.cancelReason}</dd>
            </>
          ) : null}
        </dl>

        <ul className="flex flex-col divide-y divide-gu-line border-y border-gu-line">
          {order.lines.map((l, i) => (
            <li key={i} className="flex justify-between gap-4 py-2 text-body">
              <span>
                {l.quantity} × {l.productName}
                {l.variantLabel !== 'Unico' ? ` (${l.variantLabel})` : ''}
                {l.gift ? (
                  <span className="block text-caption text-gu-ink-muted">
                    Oferta (código de oferta)
                  </span>
                ) : null}
                {l.specialPrice ? (
                  <span className="block text-caption text-gu-ink-muted">
                    Preço especial {formatEuros(l.specialPrice.unitPriceCents)}/un. em vez de{' '}
                    {formatEuros(l.unitPriceCents)} · {l.specialPrice.reason}
                    {l.specialPriceBy ? ` · por ${l.specialPriceBy}` : ''}
                  </span>
                ) : null}
                {l.forLogin ? (
                  <span className="block text-caption text-gu-ink-muted">
                    Quota do jovem {l.forLogin}, paga pelo encarregado
                  </span>
                ) : null}
                {l.personalization ? (
                  <span className="block text-caption text-gu-ink-muted">
                    Personalização:{' '}
                    {[l.personalization.name, l.personalization.number]
                      .filter((v) => v !== null)
                      .join(' ')}
                  </span>
                ) : null}
              </span>
              <span>{formatEuros(l.lineTotalCents)}</span>
            </li>
          ))}
        </ul>
        <p className="text-right font-semibold">
          {order.discountCents > 0 ? (
            <span className="mr-4 font-normal text-gu-ink-muted">
              Desconto -{formatEuros(order.discountCents)}
            </span>
          ) : null}
          Total {formatEuros(order.totalCents)}
        </p>

        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div className="flex flex-wrap gap-2">
          {order.status === 'pending_payment' ? (
            <>
              <Button disabled={busy} onClick={() => void act('received')}>
                Marcar pagamento recebido
              </Button>
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => void act('cancel', { reason: null })}
              >
                Cancelar e devolver stock
              </Button>
            </>
          ) : null}
          {order.status === 'paid' ? (
            <Button disabled={busy} onClick={() => void act('delivered')}>
              Marcar entregue
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
